import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Play } from "lucide-react";
import { dateTime } from "@/lib/format";

const FAMILY = { slots: "slots", table: "cards", dice: "dice", roulette: "roulette", blackjack: "cards", baccarat: "cards", cards: "cards" };
const playId = (g) => {
  const n = [...g.name].reduce((s, ch) => s + ch.charCodeAt(0), 0);
  const fam = /wheel/i.test(g.name) ? "wheel" : FAMILY[g.category] || "slots";
  const max = fam === "slots" ? 20 : 10;
  return `${fam}-${String((n % max) + 1).padStart(2, "0")}`;
};

export const TeamBadge = ({ team, big }) => {
  const [err, setErr] = useState(false);
  const cls = big ? "h-16 w-16 text-base" : "h-9 w-9 text-xs";
  if (team.flag && !err) return <img data-testid="team-flag" src={team.flag} alt={`${team.name} flag`} onError={() => setErr(true)} className={`${cls} shrink-0 rounded-full border-2 border-cr-gold/50 object-cover`} />;
  return <span className={`grid shrink-0 place-items-center rounded-full border border-cr-gold/40 bg-cr-gold/10 font-display font-bold text-cr-gold ${cls}`}>{team.short}</span>;
};

const CARD_IMG = ["/brand/cricket/hero.jpg", "/brand/cricket/bowler.jpg", "/brand/cricket/stumps.jpg", "/brand/cricket/trophy.jpg"];

export const LiveIndicator = () => (
  <span className="inline-flex items-center gap-1.5 rounded bg-cr-danger/15 px-2 py-0.5 text-[11px] font-bold uppercase text-cr-danger">
    <span className="live-dot h-1.5 w-1.5 rounded-full bg-cr-danger" />Live
  </span>
);

export const MatchState = ({ m }) => (m.is_live
  ? <span className="flex items-center gap-2"><LiveIndicator /><span className="truncate">{m.status}</span></span>
  : <span>{m.state === "upcoming" ? dateTime(m.start) : m.status}</span>);

export const MatchCard = ({ m }) => (
  <Link to={`/sports/match/${m.id}`} data-testid={`match-card-${m.id}`} className="card-cr group flex flex-col overflow-hidden transition-colors hover:border-cr-gold/50">
    <div className="relative h-24 overflow-hidden">
      <img src={CARD_IMG[m.id % 4]} alt="" loading="lazy" className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-cr-surface via-cr-surface/40 to-transparent" />
      <div className="absolute inset-x-4 top-3 flex items-center justify-between gap-2 text-xs text-cr-ink/90">
        <span className="truncate rounded bg-black/60 px-2 py-0.5">{m.league}</span><span className="rounded bg-black/60 px-2 py-0.5"><MatchState m={m} /></span>
      </div>
    </div>
    <div className="flex flex-col gap-4 p-4 pt-1">
    {[m.home, m.away].map((t, i) => (
      <div key={i} className="flex items-center gap-3">
        <TeamBadge team={t} />
        <span className="flex-1 truncate font-semibold">{t.name}</span>
        {t.score && <span className="font-display text-sm font-bold">{t.score}</span>}
      </div>
    ))}
    <div className="flex items-center justify-between border-t border-cr-line pt-3 text-xs text-cr-muted">
      <span className="truncate">{m.venue || "Venue TBA"}</span>
      {m.can_predict ? <span data-testid={`match-predict-${m.id}`} className="rounded bg-cr-gold px-2 py-1 font-bold text-black">Predict · {m.odds}x</span>
        : <span className="capitalize">{m.state}</span>}
    </div>
    </div>
  </Link>
);

export const GameCard = ({ game, fav, onFav }) => (
  <div data-testid={`game-card-${game.id}`} className="group relative overflow-hidden rounded-xl border border-cr-line bg-cr-surface">
    <Link to={`/arcade?play=${playId(game)}`} data-testid={`game-play-${game.id}`} className="relative block aspect-[4/3] overflow-hidden">
      <img src={game.image} alt={game.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      {game.section === "live" && <div className="absolute left-2 top-2"><LiveIndicator /></div>}
      <div className="absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover:opacity-100">
        <span className="flex items-center gap-1 rounded-full bg-cr-gold/90 px-3 py-1 text-xs font-bold text-black"><Play className="h-3 w-3" />Play now</span>
      </div>
    </Link>
    {onFav && (
      <button data-testid={`game-fav-${game.id}`} aria-label="Toggle favourite" onClick={() => onFav(game.id)} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5">
        <Heart className={`h-4 w-4 ${fav ? "fill-cr-gold text-cr-gold" : "text-white"}`} />
      </button>
    )}
    <div className="p-3">
      <p className="truncate font-display font-semibold">{game.name}</p>
      <p className="truncate text-xs text-cr-muted">{game.provider} · <span className="capitalize">{game.category}</span></p>
    </div>
  </div>
);
